import React, { useState, useEffect, useCallback } from 'react';
import { 
  Dialog, DialogContent, Box, IconButton, Typography,
  Stepper, Step, StepLabel, Button, Snackbar, Alert
} from '@mui/material';
import { Close as CloseIcon, AutoAwesome as AIIcon } from '@mui/icons-material';
import axios from 'axios';

// Components
import AIImageUpload from './AIImageUpload';
import AITradeForm from './AITradeForm';
import AITradePreview from './AITradePreview';

// Hooks
import { useImageAnalysis } from '../../hooks/useImageAnalysis';
import { useSettings } from '../../contexts/SettingsContext';

const AITradeModal = ({ open, onClose, onTradeCreated }) => {
  const [strategies, setStrategies] = useState([]);
  const [trades, setTrades] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [currentStep, setCurrentStep] = useState(0);
  const { settings, refreshSettings } = useSettings();

  const {
    imageFile, previewUrl, tradeData, setTradeData, loadingModel, metrics,
    handleFileSelect, handleDrop, analyzeImage, createTrade, clearImage
  } = useImageAnalysis();

  const steps = ['Upload Screenshot', 'AI Analysis', 'Trade Details', 'Review & Save'];

  useEffect(() => {
    if (open) {
      fetchStrategies();
      fetchTrades();
      refreshSettings(); // Refresh settings to get latest subscription data
    }
  }, [open, refreshSettings]);

  const fetchStrategies = async () => {
    try {
      const token = localStorage.getItem('idToken');
      const res = await axios.get('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/createTradeStrategy', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStrategies(res.data);
    } catch (err) {
      console.error('Error fetching strategies:', err);
    }
  };

  const fetchTrades = async () => {
    try {
      const token = localStorage.getItem('idToken');
      const res = await axios.get('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/trades', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrades(res.data.sort((a, b) => new Date(b.tradeDate) - new Date(a.tradeDate)));
    } catch (err) {
      console.error('Error fetching trades:', err);
    }
  };

  const handleAnalyze = useCallback(async () => {
    // Check AI quota limits
    const aiQuota = settings?.subscription?.aiTradeAssistQuota;
    if (aiQuota && aiQuota.used >= aiQuota.totalLimit) {
      setSnackbar({ 
        open: true, 
        message: `AI analysis limit reached (${aiQuota.used}/${aiQuota.totalLimit}). Upgrade your plan for more.`, 
        severity: 'warning' 
      });
      return;
    }
    
    const result = await analyzeImage();
    setSnackbar({ open: true, message: result.message, severity: result.success ? 'success' : 'error' });
    if (result.success) {
      setCurrentStep(2);
    }
  }, [analyzeImage, settings?.subscription?.aiTradeAssistQuota]);

  const handleSaveTrade = useCallback(async () => {
    const result = await createTrade();
    setSnackbar({ open: true, message: result.message, severity: result.success ? 'success' : 'error' });
    if (result.success) {
      // Dispatch event to refresh dashboard
      window.dispatchEvent(new CustomEvent('tradeCreated'));
      onTradeCreated?.();
      handleClose();
    }
  }, [createTrade, onTradeCreated]);

  const handleTradeDataChange = useCallback((field, value) => {
    setTradeData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleClose = () => {
    setCurrentStep(0);
    onClose();
  };

  const handleNext = () => {
    if (currentStep === 0 && imageFile) {
      setCurrentStep(1);
    } else if (currentStep === 2) {
      setCurrentStep(3);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <AIImageUpload
            previewUrl={previewUrl}
            onFileSelect={handleFileSelect}
            onDrop={handleDrop}
            hasImage={!!imageFile}
            onNext={() => setCurrentStep(1)}
            onClear={clearImage}
          />
        );
      case 1:
        return (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <AIIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              AI Analysis Ready
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              Click analyze to extract trade data from your screenshot
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Usage: {settings?.subscription?.aiTradeAssistQuota?.used || 0}/{settings?.subscription?.aiTradeAssistQuota?.totalLimit || 0}
              {!settings?.subscription?.aiTradeAssistQuota && ' (loading...)'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                onClick={handleAnalyze}
                disabled={loadingModel}
                variant="contained"
                size="large"
                sx={{ minWidth: 150 }}
              >
                {loadingModel ? 'Analyzing...' : 'Analyze Screenshot'}
              </Button>
              <Button
                onClick={() => setCurrentStep(2)}
                variant="outlined"
                size="large"
                sx={{ minWidth: 150 }}
              >
                Skip Analysis
              </Button>
            </Box>
          </Box>
        );
      case 2:
        return (
          <AITradeForm
            tradeData={tradeData}
            onChange={handleTradeDataChange}
            strategies={strategies}
            trades={trades}
            metrics={metrics}
          />
        );
      case 3:
        return (
          <AITradePreview
            tradeData={tradeData}
            previewUrl={previewUrl}
            metrics={metrics}
            onSave={handleSaveTrade}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh', borderRadius: 2 }
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AIIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6" fontWeight="bold">
              AI Trade Creator
            </Typography>
          </Box>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stepper activeStep={currentStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <DialogContent sx={{ p: 3, overflow: 'auto' }}>
          {renderStepContent()}
        </DialogContent>

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider'
        }}>
          <Button
            onClick={handleBack}
            disabled={currentStep === 0}
            variant="outlined"
          >
            Back
          </Button>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            {currentStep === 3 && (
              <Button
                onClick={handleSaveTrade}
                variant="contained"
                color="success"
                size="large"
              >
                Save Trade
              </Button>
            )}
            
            {currentStep < 3 && (
              <Button
                onClick={handleNext}
                disabled={
                  (currentStep === 0 && !imageFile) ||
                  (currentStep === 2 && (!tradeData.symbol || !tradeData.entryPrice))
                }
                variant="contained"
              >
                Next
              </Button>
            )}
          </Box>
        </Box>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AITradeModal;